import { Users, Radio } from "lucide-react";

import { ControlButton } from "../common/ControlButton";
import { TrackedDropdownMenu } from "../common/TrackedDropdownMenu";
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/clientWeb/components/ui/dropdown-menu";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";

/**
 * The webinar-only controls, split out of the Apps menu.
 *
 * Starting a broadcast, surveys, branding and engagement controls are
 * meaningless in an ordinary meeting — they were sitting in Apps for everyone,
 * padding a menu most people could not use any of. They live here instead, and
 * ControlBar only renders this button for a webinar, so the option is absent
 * rather than present-and-inert.
 */
interface WebinarMenuProps {
  onStartWebinar?: () => void;
  onStopWebinar?: () => void;
  onOpenSurveys?: () => void;
  onOpenBranding?: () => void;
  onOpenEngagementControls?: () => void;
  isWebinarLive?: boolean;
}

export function WebinarMenu({
  onStartWebinar,
  onStopWebinar,
  onOpenSurveys,
  onOpenBranding,
  onOpenEngagementControls,
  isWebinarLive = false,
}: WebinarMenuProps) {
  const t = useTranslation();

  return (
    <TrackedDropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* This div is load-bearing, exactly as in AppsMenu. `asChild` clones
            its child and needs a ref to anchor and toggle the menu;
            ControlButton does not forward one, so without a plain element here
            the trigger never attaches and clicking the button does nothing. */}
        <div>
          <ControlButton onClick={() => { }} label={"Webinar"}>
            {/* Live state on the button itself — the menu is shut most of the
                time, and "is this broadcasting?" is what a host needs at a
                glance. */}
            <Radio className={`h-5 w-5 text-white ${isWebinarLive ? "text-red-500 animate-pulse" : ""}`} />
          </ControlButton>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[200px]">
        {isWebinarLive ? (
          <DropdownMenuItem
            className="gap-3 bg-red-50 text-red-600 focus:bg-red-100 focus:text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:focus:bg-red-500/20 dark:focus:text-red-300"
            onSelect={() => onStopWebinar?.()}
          >
            <Radio className="h-4 w-4 animate-pulse" />
            <span>{t("controlBar.stopWebinar")}</span>
            <span className="ml-auto text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded">LIVE!</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className="gap-3" onSelect={() => onStartWebinar?.()}>
            <Radio className="h-4 w-4" />
            <span>{t("controlBar.startWebinar")}</span>
          </DropdownMenuItem>
        )}
        {/* Each item only with its callback: ControlBar withholds these on a
            phone, and an item that opens nothing should not be listed. */}
        {onOpenSurveys && (
          <DropdownMenuItem className="gap-3" onSelect={() => onOpenSurveys()}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <span>{t("controlBar.surveys")}</span>
          </DropdownMenuItem>
        )}
        {onOpenBranding && (
          <DropdownMenuItem className="gap-3" onSelect={() => onOpenBranding()}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <span>{t("controlBar.branding")}</span>
          </DropdownMenuItem>
        )}
        {onOpenEngagementControls && (
          <DropdownMenuItem className="gap-3" onSelect={() => onOpenEngagementControls()}>
            <Users className="h-4 w-4" />
            <span>{t("controlBar.engagementControls")}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </TrackedDropdownMenu>
  );
}
